import type { NetworkErrorReport } from '@shared/networkError';

import type { Org } from '@/api-svc-types';
import { doCookieAuth } from '@/BaseVue';
import router from '@/router';
import { MUT_PARENT_AUTH_TOKEN, store } from '@/store';

import { stringifyParams } from './endpointUrlUtil';

let requestId = 0;
const responses: { [key: number]: { resolve: (value: string) => void; reject: (reason?: any) => void } } = {};

function checkParentOrigin() {
  const parentOrigin = process.env.VUE_APP_PARENT_WEB_APP;
  if (!parentOrigin) throw new Error('VUE_APP_PARENT_WEB_APP is not set');
  return parentOrigin;
}

export function requestTokenFromParent(): Promise<string> {
  const currentRequestId = ++requestId;
  const parentOrigin = checkParentOrigin();
  window.parent.postMessage({ request: 'token', requestId: currentRequestId }, parentOrigin);

  return new Promise((resolve, reject) => {
    responses[currentRequestId] = { resolve, reject };

    // Add 5 second timeout to prevent hanging requests
    setTimeout(() => {
      if (responses[currentRequestId]) {
        delete responses[currentRequestId]; // Clean up to prevent memory leak
        reject(new Error('Token request timeout - parent did not respond within 5 seconds'));
      }
    }, 5000);
  });
}

/**
 * True when v2 is running inside another window (e.g., the v3 shell iframes
 * v2 for /rules). Extracted as a helper so consumers and tests can mock it
 * cleanly — jsdom's `window.top` is a WindowProxy that ignores
 * `Object.defineProperty`, making direct `window.self !== window.top` checks
 * hostile to mocking in component tests.
 */
export function isIframed(): boolean {
  return window.self !== window.top;
}

export function requestParentToKeepActive(event: string, pauseWatcher?: boolean) {
  if (window.self !== window.top) {
    const parentOrigin = checkParentOrigin();
    window.parent.postMessage({ request: 'keepActive', payload: { event, pauseWatcher } }, parentOrigin);
  }
}

export function requestParentToNavigate(path: string, params?: any) {
  if (window.self !== window.top) {
    const parentOrigin = checkParentOrigin();
    const stringParams = typeof params !== 'string' ? stringifyParams(params) : params;
    window.parent.postMessage({ request: 'navigate', path, params: stringParams }, parentOrigin);
  }
}

export function notifyParentRefreshRequired() {
  if (window.self !== window.top) {
    const parentOrigin = checkParentOrigin();
    window.parent.postMessage({ request: 'refreshRequired' }, parentOrigin);
  }
}

/**
 * Tells the v3 shell this window is booted and listening. v3 pushes `orgChange` exactly
 * once, when ITS org finishes loading — on a fresh page load that is usually before this
 * bundle has even executed, so the message lands on a window with no listener and is
 * lost. v3 answers `ready` with a fresh `orgChange` so the two apps always converge.
 */
export function notifyParentReady() {
  if (isIframed()) {
    const parentOrigin = checkParentOrigin();
    window.parent.postMessage({ request: 'ready' }, parentOrigin);
  }
}

export type InvalidateEntity =
  | 'wallets'
  | 'connections'
  | 'categories'
  | 'contacts'
  | 'inventories'
  | 'inventoryGroups';

export function notifyParentStoreInvalidated(entities: InvalidateEntity | InvalidateEntity[]) {
  if (window.self !== window.top) {
    const parentOrigin = checkParentOrigin();
    const list = Array.isArray(entities) ? entities : [entities];
    window.parent.postMessage({ request: 'invalidate', entities: list }, parentOrigin);
  }
}

export function reportErrorToParent(error: unknown, source: string, info?: string) {
  if (window.self === window.top) return;
  try {
    const parentOrigin = checkParentOrigin();
    const err = error instanceof Error ? error : new Error(typeof error === 'string' ? error : 'Unknown error');
    window.parent.postMessage(
      {
        request: 'reportError',
        payload: {
          name: err.name,
          message: err.message,
          stack: err.stack,
          source,
          info,
          url: window.location.href,
        },
      },
      parentOrigin
    );
  } catch {
    // Swallow — telemetry must never crash the error path
  }
}

export function reportNetworkErrorToParent(report: NetworkErrorReport) {
  if (!isIframed()) return;
  try {
    window.parent.postMessage({ request: 'reportNetworkError', payload: report }, checkParentOrigin());
  } catch {
    // Swallow — telemetry must never crash the error path
  }
}

// Listening on coming messages
window.addEventListener('message', async (event: MessageEvent) => {
  if (event.origin !== process.env.VUE_APP_PARENT_WEB_APP) return; // Check origin
  const data = event.data as { type: string; responseId: number; token: string; currentOrg?: string; path: string };
  const type = data.type;
  switch (type) {
    case 'auth': {
      const responseHandler = responses[data.responseId];
      if (responseHandler) {
        responseHandler.resolve(data.token);
        delete responses[data.responseId]; // Clean up
      }
      break;
    }
    case 'orgChange': {
      const orgId = data.currentOrg;
      // v3 re-sends its org whenever it (re)loads one and in reply to `ready`; when we
      // are already on that org with that token there is nothing to switch.
      if (data.token === store.state.parentAuthToken && orgId === store.state.currentOrg?.id) break;
      await store.commit(MUT_PARENT_AUTH_TOKEN, data.token);
      await doCookieAuth();
      // The org list was loaded at boot; an org created (or joined) since then is unknown
      // here and MUT_SET_CURRENT_ORG would silently keep the previous org — under a token
      // for the new one. Reload the list first; updateOrgs re-selects the token's org
      // itself, so only switch again if it did not land there.
      let orgsRefreshed = false;
      if (orgId && store.state.orgs && !store.state.orgs.some((o: Org) => o.id === orgId)) {
        await store.dispatch('updateOrgs');
        orgsRefreshed = true;
      }
      if (!(orgsRefreshed && store.state.currentOrg?.id === orgId)) {
        await store.dispatch('setCurrentOrg', orgId);
      }
      break;
    }
    case 'navigate':
      router.push(data.path);
      break;
    default:
      break;
  }
});
